import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Send, MapPin, Mail, Clock } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTheme } from '../../context/ThemeContext';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Telegram username (no phone number exposed)
const TELEGRAM_USERNAME = 'jqf_info';

const contactInfo = [
  { icon: MapPin, label: 'Dirección', value: 'Gamonal\n09007 Burgos, España' },
  { icon: Mail, label: 'Email', value: 'info@jonquintano.com' },
  { icon: Clock, label: 'Horario', value: 'Lun - Vie: 9:00 - 19:00' },
];

// Generate random math challenge
const generateChallenge = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return { num1, num2, answer: num1 + num2 };
};

export const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState(''); // Hidden field - bots fill this
  const [challenge, setChallenge] = useState(generateChallenge);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [formLoadTime] = useState(Date.now()); // Track when form loaded
  const { theme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Anti-bot check 1: Honeypot field should be empty
    if (honeypot) {
      toast.error('Error al enviar el mensaje');
      return;
    }
    
    // Anti-bot check 2: Time validation (minimum 3 seconds)
    const timeTaken = Date.now() - formLoadTime;
    if (timeTaken < 3000) {
      toast.error('Por favor, tómate tu tiempo para rellenar el formulario');
      return;
    }
    
    // Anti-bot check 3: Math challenge
    if (parseInt(captchaAnswer) !== challenge.answer) {
      toast.error('La respuesta al cálculo no es correcta');
      setChallenge(generateChallenge()); // Generate new challenge
      setCaptchaAnswer('');
      return;
    }
    
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Por favor, completa todos los campos');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await response.json();
      toast.success(data.message);
      setForm({ name: '', email: '', subject: '', message: '' });
      setCaptchaAnswer('');
      setChallenge(generateChallenge()); // New challenge for next submission
    } catch (error) {
      toast.error('Error al enviar el mensaje');
    } finally {
      setLoading(false);
    }
  };

  const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const mapClass = theme === 'dark' ? 'leaflet-tiles-dark' : '';

  // Burgos - Gamonal coordinates
  const position = [42.3514, -3.6754];

  const openTelegram = () => {
    window.open(`https://t.me/${TELEGRAM_USERNAME}`, '_blank');
  };

  return (
    <section id="contacto" className="py-20 md:py-32 bg-muted" data-testid="contact-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-4">
            Contacto
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
            Hablemos
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            ¿Tienes un proyecto en mente? Me encantaría escucharte y ayudarte a hacerlo realidad.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="order-2 lg:order-1"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from humans, bots fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="absolute opacity-0 pointer-events-none"
                tabIndex={-1}
                autoComplete="off"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                    Nombre
                  </label>
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none border border-border bg-background/80 px-4 py-3 focus-visible:ring-0 focus-visible:border-foreground"
                    placeholder="Tu nombre"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none border border-border bg-background/80 px-4 py-3 focus-visible:ring-0 focus-visible:border-foreground"
                    placeholder="tu@email.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                  Asunto
                </label>
                <Input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="rounded-none border border-border bg-background/80 px-4 py-3 focus-visible:ring-0 focus-visible:border-foreground"
                  placeholder="¿En qué puedo ayudarte?"
                  data-testid="contact-subject"
                />
              </div>
              
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                  Mensaje
                </label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-none border border-border bg-background/80 px-4 py-3 focus-visible:ring-0 focus-visible:border-foreground min-h-[150px]"
                  placeholder="Cuéntame sobre tu proyecto..."
                  data-testid="contact-message"
                />
              </div>
              
              {/* Math CAPTCHA */}
              <div className="bg-background/60 border border-border p-4">
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-3">
                  Verificación anti-spam
                </label>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">
                    ¿Cuánto es {challenge.num1} + {challenge.num2}?
                  </span>
                  <Input
                    type="number"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    className="w-24 rounded-none border border-border bg-background/80 px-3 py-2 focus-visible:ring-0 focus-visible:border-foreground"
                    placeholder="?"
                    data-testid="contact-captcha"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="rounded-none border border-foreground bg-foreground text-background hover:bg-transparent hover:text-foreground transition-colors duration-300 px-8 py-3 uppercase tracking-widest text-xs w-full md:w-auto"
                data-testid="contact-submit"
              >
                {loading ? 'Enviando...' : 'Enviar Mensaje'}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-6 mt-12 pt-12 border-t border-border">
              {contactInfo.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <info.icon className="w-5 h-5 mt-1 text-muted-foreground" />
                  <div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-1">
                      {info.label}
                    </span>
                    <span className="text-sm whitespace-pre-line">{info.value}</span>
                  </div>
                </div>
              ))}
              
              {/* Telegram Button */}
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 mt-1 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                <div>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-1">
                    Contacto Directo
                  </span>
                  <button
                    onClick={openTelegram}
                    className="inline-flex items-center gap-2 text-sm bg-[#0088cc] hover:bg-[#006699] text-white px-4 py-2 transition-colors"
                    data-testid="telegram-btn"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    Telegram
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2 h-[400px] lg:h-full min-h-[400px] border border-border overflow-hidden"
          >
            <MapContainer
              center={position}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
              className={mapClass}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url={tileUrl}
              />
              <Marker position={position} icon={customIcon}>
                <Popup>
                  <div className="p-2">
                    <h4 className="font-medium mb-1">Jon Quintano Fotografía</h4>
                    <p className="text-xs text-muted-foreground">Gamonal, 09007 Burgos</p>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
